import React, { useState } from 'react';
import { Modal, Upload, Button, Progress, List, Typography, Space, message } from 'antd';
import { InboxOutlined, CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { uploadDicomStudies } from '../../utils/helper';
import './styles.css';

const { Dragger } = Upload;
const { Text, Title } = Typography;

interface DicomUploadModalProps {
  visible: boolean;
  onCancel: () => void;
  studyUID?: string;
  onUploadSuccess?: () => void;
}

interface FileWithValidation {
  file: File;
  uid: string;
  validationStatus: 'pending' | 'validating' | 'valid' | 'invalid';
  validationMessage?: string;
}

interface UploadResult {
  totalFiles: number;
  uploadedCount: number;
  failedCount: number;
  invalidFiles: Array<{ filename: string; reason: string }>;
  batches: Array<{ batchIndex: number; fileCount: number; success: boolean }>;
}

const DicomUploadModal: React.FC<DicomUploadModalProps> = ({
  visible,
  onCancel,
  studyUID,
  onUploadSuccess,
}) => {
  const [fileList, setFileList] = useState<FileWithValidation[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);

  // Validate DICOM file by checking for DICM magic bytes at offset 128
  const validateDicomFile = async (file: File): Promise<{ valid: boolean; message: string }> => {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const arrayBuffer = e.target?.result as ArrayBuffer;

          if (!arrayBuffer || arrayBuffer.byteLength < 132) {
            resolve({ valid: false, message: 'File too small to be a valid DICOM file' });
            return;
          }

          // Check for DICM magic bytes at offset 128
          const view = new Uint8Array(arrayBuffer, 128, 4);
          const magicBytes = String.fromCharCode(...view);

          if (magicBytes === 'DICM') {
            resolve({ valid: true, message: 'Valid DICOM file' });
          } else {
            resolve({ valid: false, message: 'DICM magic bytes not found (not a valid DICOM file)' });
          }
        } catch (error) {
          resolve({ valid: false, message: 'Error reading file' });
        }
      };

      reader.onerror = () => {
        resolve({ valid: false, message: 'Error reading file' });
      };

      // Read first 132 bytes to check magic bytes
      const blob = file.slice(0, 132);
      reader.readAsArrayBuffer(blob);
    });
  };

  // Handle file selection
  const handleFileSelect = async (file: File) => {
    const fileWithValidation: FileWithValidation = {
      file: file,
      uid: `${file.name}-${Date.now()}-${Math.random()}`,
      validationStatus: 'validating' as const,
    };

    // Add file to list with validating status
    setFileList((prev) => [...prev, fileWithValidation]);

    // Validate the file
    const validation = await validateDicomFile(file);

    // Update file validation status
    setFileList((prev) =>
      prev.map((f) =>
        f.uid === fileWithValidation.uid
          ? {
              ...f,
              validationStatus: validation.valid ? 'valid' : 'invalid',
              validationMessage: validation.message,
            }
          : f
      )
    );

    return false; // Prevent auto upload
  };

  // Upload files to server
  const handleUpload = async () => {
    const validFiles = fileList.filter((f) => f.validationStatus === 'valid');

    if (validFiles.length === 0) {
      message.warning('Please select valid DICOM files to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadResult(null);

    try {
      // Extract actual File objects from FileWithValidation
      const files = validFiles.map((f) => f.file);

      const result = await uploadDicomStudies(files, (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percentCompleted);
      });

      setUploadResult(result);

      if (result.failedCount === 0 && result.invalidFiles.length === 0) {
        message.success(`Successfully uploaded ${result.uploadedCount} files`);

        // Clear file list after successful upload
        setTimeout(() => {
          setFileList([]);
          setUploadResult(null);
          onUploadSuccess?.();
          onCancel();
        }, 2000);
      } else {
        message.warning(
          `Upload completed with issues: ${result.uploadedCount} succeeded, ${result.failedCount} failed`
        );
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Remove file from list
  const handleRemoveFile = (uid: string) => {
    setFileList((prev) => prev.filter((f) => f.uid !== uid));
  };

  // Clear all files
  const handleClearAll = () => {
    setFileList([]);
    setUploadResult(null);
  };

  // Reset modal state on close
  const handleClose = () => {
    if (!uploading) {
      setFileList([]);
      setUploadResult(null);
      onCancel();
    }
  };

  const validFileCount = fileList.filter((f) => f.validationStatus === 'valid').length;
  const invalidFileCount = fileList.filter((f) => f.validationStatus === 'invalid').length;

  return (
    <Modal
      title="Upload DICOM Images"
      open={visible}
      onCancel={handleClose}
      width={700}
      footer={[
        <Button key="clear" onClick={handleClearAll} disabled={uploading || fileList.length === 0}>
          Clear All
        </Button>,
        <Button key="cancel" onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>,
        <Button
          key="upload"
          type="primary"
          onClick={handleUpload}
          loading={uploading}
          disabled={validFileCount === 0 || uploading}
        >
          Upload {validFileCount > 0 && `(${validFileCount} files)`}
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Upload Dragger */}
        <Dragger
          multiple
          beforeUpload={handleFileSelect}
          fileList={[]}
          disabled={uploading}
          showUploadList={false}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag files to this area to upload</p>
          <p className="ant-upload-hint">
            Support for multiple DICOM files. Files will be validated for DICM magic bytes.
          </p>
        </Dragger>

        {/* File Statistics */}
        {fileList.length > 0 && (
          <div className="dicom-upload-stats">
            <Space size="large">
              <Text>
                <strong>Total:</strong> {fileList.length}
              </Text>
              <Text type="success">
                <CheckCircleOutlined /> Valid: {validFileCount}
              </Text>
              {invalidFileCount > 0 && (
                <Text type="danger">
                  <CloseCircleOutlined /> Invalid: {invalidFileCount}
                </Text>
              )}
            </Space>
          </div>
        )}

        {/* Upload Progress */}
        {uploading && (
          <div>
            <Text strong>Uploading files...</Text>
            <Progress percent={uploadProgress} status="active" />
          </div>
        )}

        {/* Upload Result */}
        {uploadResult && !uploading && (
          <div className="dicom-upload-result">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>Upload Results:</Text>
              <Space size="large">
                <Text type="success">
                  <CheckCircleOutlined /> Uploaded: {uploadResult.uploadedCount}
                </Text>
                {uploadResult.failedCount > 0 && (
                  <Text type="danger">
                    <CloseCircleOutlined /> Failed: {uploadResult.failedCount}
                  </Text>
                )}
              </Space>

              {uploadResult.invalidFiles && uploadResult.invalidFiles.length > 0 && (
                <div>
                  <Text type="danger" strong>
                    Invalid Files:
                  </Text>
                  <List
                    size="small"
                    bordered
                    dataSource={uploadResult.invalidFiles}
                    renderItem={(item) => (
                      <List.Item>
                        <Text>{item.filename}</Text>
                        <Text type="secondary"> - {item.reason}</Text>
                      </List.Item>
                    )}
                  />
                </div>
              )}
            </Space>
          </div>
        )}

        {/* File List */}
        {fileList.length > 0 && !uploadResult && (
          <div className="dicom-upload-file-list">
            <Text strong>Selected Files:</Text>
            <List
              size="small"
              bordered
              dataSource={fileList}
              renderItem={(file) => (
                <List.Item
                  actions={[
                    !uploading && (
                      <Button
                        type="link"
                        danger
                        size="small"
                        onClick={() => handleRemoveFile(file.uid)}
                      >
                        Remove
                      </Button>
                    ),
                  ]}
                >
                  <Space>
                    {file.validationStatus === 'validating' && <LoadingOutlined />}
                    {file.validationStatus === 'valid' && (
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    )}
                    {file.validationStatus === 'invalid' && (
                      <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                    )}
                    <div>
                      <Text>{file.file.name}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {(file.file.size / 1024 / 1024).toFixed(2)} MB
                        {file.validationMessage && ` - ${file.validationMessage}`}
                      </Text>
                    </div>
                  </Space>
                </List.Item>
              )}
            />
          </div>
        )}
      </Space>
    </Modal>
  );
};

export default DicomUploadModal;
